import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';
import { useAuth } from '../context/AuthContext';

const { FiX, FiPlus, FiTrash2, FiBriefcase, FiMapPin, FiUser, FiInfo } = FiIcons;

export default function PackedBagModal({ bag, onClose, onSave }) {
  const { state } = useFamilyContext();
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    child_id: bag?.child_id || '',
    destination: bag?.destination || '',
    notes: bag?.notes || '',
    items: bag?.items || []
  });

  const [newItemName, setNewItemName] = useState('');

  const addItem = () => {
    if (!newItemName.trim()) return;
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { name: newItemName.trim(), is_packed: true, is_returned: false }]
    }));
    setNewItemName('');
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.child_id || !formData.destination) return;
    onSave(formData);
  };

  const children = state.familyMembers.filter(m => m.role === 'child' || m.role === 'member');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]"
      >
        <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-blue-50/50 dark:bg-blue-900/20">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
            <SafeIcon icon={FiBriefcase} className="mr-2 text-blue-600" />
            {bag ? 'Tasche bearbeiten' : 'Neue Tasche packen'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-full transition-colors">
            <SafeIcon icon={FiX} className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <SafeIcon icon={FiUser} className="mr-1.5 w-4 h-4" /> Kind auswählen
            </label>
            <select
              required
              value={formData.child_id}
              onChange={e => setFormData({...formData, child_id: e.target.value})}
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-blue-500 dark:text-white"
            >
              <option value="">Bitte wählen...</option>
              {children.map(child => (
                <option key={child.id} value={child.id}>{child.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <SafeIcon icon={FiMapPin} className="mr-1.5 w-4 h-4" /> Ziel / Wohin geht es?
            </label>
            <input
              required
              type="text"
              placeholder="z.B. Oma & Opa, Papa, Fußballcamp"
              value={formData.destination}
              onChange={e => setFormData({...formData, destination: e.target.value})}
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-blue-500 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Inhalt der Tasche</label>
            <div className="flex space-x-2 mb-3">
              <input
                type="text"
                placeholder="Gegenstand hinzufügen..."
                value={newItemName}
                onChange={e => setNewItemName(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addItem())}
                className="flex-1 px-4 py-2 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-blue-500 dark:text-white"
              />
              <button 
                type="button"
                onClick={addItem}
                className="p-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-500/20"
              >
                <SafeIcon icon={FiPlus} className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
              {formData.items.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl group border border-transparent hover:border-gray-200 dark:hover:border-gray-700 transition-all">
                  <span className="text-sm text-gray-700 dark:text-gray-300">{item.name}</span>
                  <button 
                    type="button"
                    onClick={() => removeItem(index)}
                    className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {formData.items.length === 0 && (
                <p className="text-center py-4 text-xs text-gray-400 italic">Noch keine Sachen eingepackt.</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <SafeIcon icon={FiInfo} className="mr-1.5 w-4 h-4" /> Notizen
            </label>
            <textarea
              rows="2"
              placeholder="Besondere Hinweise..."
              value={formData.notes}
              onChange={e => setFormData({...formData, notes: e.target.value})}
              className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-blue-500 dark:text-white"
            />
          </div>

          <div className="pt-4">
            <button
              type="submit"
              className="w-full py-3 bg-blue-600 text-white rounded-2xl font-bold hover:bg-blue-700 transition-all shadow-xl shadow-blue-500/25 active:scale-[0.98]"
            >
              Tasche speichern
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}